/**
 * Duplicate Profile Poller (Phase 9)
 * Polls for pending duplicate profile confirmations
 * Similar to auto-profile poller but for duplicates
 */

import axios from 'axios';

class DuplicateProfilePoller {
  constructor() {
    this.activePollers = new Map(); // sessionId -> interval
    this.pollInterval = 10000; // 10 seconds (reduced from 5s to avoid rate limiting)
  }

  /**
   * Start polling for a proxy session
   */
  start(sessionId, onDuplicateDetected) {
    // Don't create duplicate pollers
    if (this.activePollers.has(sessionId)) {
      console.log('[DuplicatePoller] Already polling session:', sessionId);
      return;
    }

    console.log('[DuplicatePoller] Starting poller for session:', sessionId);

    const poll = async () => {
      try {
        const response = await axios.get(`/api/proxy-sessions/${sessionId}/duplicate-profiles`);
        const pendingDuplicates = response.data || [];

        if (pendingDuplicates.length > 0) {
          console.log('[DuplicatePoller] Pending duplicates found:', pendingDuplicates.length);

          // Notify callback for each pending duplicate
          pendingDuplicates.forEach((duplicate, index) => {
            onDuplicateDetected({
              duplicate,
              sessionId,
              duplicateIndex: index
            });
          });
        }
      } catch (err) {
        // Stop polling on 401 (unauthorized) or 404 (not found)
        if (err.response?.status === 401 || err.response?.status === 404) {
          console.log(`[DuplicatePoller] Stopping poller for session ${sessionId} (${err.response?.status})`);
          this.stop(sessionId);
          return;
        }

        // Only log other errors
        console.error('[DuplicatePoller] Polling error:', err.message);
      }
    };

    // Poll immediately, then every interval
    poll();
    const intervalId = setInterval(poll, this.pollInterval);
    this.activePollers.set(sessionId, intervalId);
  }

  /**
   * Stop polling for a session
   */
  stop(sessionId) {
    const intervalId = this.activePollers.get(sessionId);
    if (intervalId) {
      clearInterval(intervalId);
      this.activePollers.delete(sessionId);
      console.log('[DuplicatePoller] Stopped poller for session:', sessionId);
    }
  }

  /**
   * Stop all pollers
   */
  stopAll() {
    for (const [sessionId, intervalId] of this.activePollers.entries()) {
      clearInterval(intervalId);
    }
    this.activePollers.clear();
    console.log('[DuplicatePoller] Stopped all pollers');
  }

  /**
   * Check if polling a session
   */
  isPolling(sessionId) {
    return this.activePollers.has(sessionId);
  }

  /**
   * Get active session count
   */
  getActiveCount() {
    return this.activePollers.size;
  }
}

// Singleton instance
const duplicateProfilePoller = new DuplicateProfilePoller();

export default duplicateProfilePoller;
